import React from "react";

import { Tooltip } from "@amphore-dev/amphore-lib";

import { convertToTime } from "@/utils/UDates";
import { cn } from "@/utils/cn";

export type TWeekHours = {
	/** Short label under the bar ("S37"). */
	label: string;
	minutes: number;
	/** The week that is still running - drawn in the full colour. */
	isCurrent?: boolean;
};

export interface IWeeklyHoursBarsProps {
	weeks: TWeekHours[];
	className?: string;
}

/**
 * One bar per week, the tallest at full height. Plain CSS: a sparkline
 * of a handful of values needs no chart library.
 */
export const WeeklyHoursBars: React.FC<IWeeklyHoursBarsProps> = ({
	weeks,
	className,
}) => {
	const max = Math.max(1, ...weeks.map((week) => week.minutes));

	return (
		<div
			className={cn(["flex h-32 items-end gap-2", className])}
			role="img"
			aria-label={weeks
				.map(
					(week) =>
						`${week.label}: ${convertToTime(week.minutes / 60)}`
				)
				.join(", ")}
		>
			{weeks.map((week) => (
				// Lib Tooltip, not a `title`: a native title never shows on
				// touch, the Tooltip opens on tap there.
				<Tooltip
					key={week.label}
					content={`${week.label} · ${convertToTime(week.minutes / 60)}`}
				>
					<div
						className="flex h-full grow flex-col items-center justify-end gap-1.5"
						data-testid="week-bar"
					>
						<span
							className={cn([
								"w-full rounded-t",
								week.isCurrent
									? "bg-primary-500"
									: "bg-primary-200",
							])}
							style={{
								height: `${Math.round((week.minutes / max) * 100)}%`,
							}}
							data-minutes={week.minutes}
						/>
						<span className="text-[11px] text-neutral-500">
							{week.label}
						</span>
					</div>
				</Tooltip>
			))}
		</div>
	);
};
